import { memo, useEffect, useState } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { styled } from '@mui/material'
import projectDefaultIcon from '../../../../assets/project_icon_default.svg'
import Tooltip from '../../../components/tooltips/Tooltip'
import { setError } from '../../../containers/BulkSheetView/lib/validation'
import { ProgramIcon } from '../../../components/icons/ProgramIcon'
import { ProgramItemGroupIcon } from '../../../components/icons/ProgramItemGroupIcon'
import {
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'
import { programItemRowService } from '../programItemRowService'

const TYPE_ICON_SIZE = 18

const renderTypeIcon = (type: ProgramManagementRow['type']) => {
  switch (type) {
    case PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM:
      return <ProgramIcon size={TYPE_ICON_SIZE} />
    case PROGRAM_MANAGEMENT_ROW_TYPE.GROUP:
      return <ProgramItemGroupIcon size={TYPE_ICON_SIZE} />
    default:
      return null
  }
}

type Props = ICellRendererParams<ProgramManagementRow> & {
  validate?: (
    value: string,
    params: ICellRendererParams<ProgramManagementRow>
  ) => string | undefined
  // プロジェクト行アイコンのクリックハンドラ（cellRendererParams 経由で渡す）。
  onProjectIconClick?: (row: ProgramManagementRow) => void
}

export const ProgramItemTreeCellRenderer = memo(
  (props: Props) => {
    const { value, data, onProjectIconClick } = props
    // DefaultCellRenderer と同様に validate を即時評価し、エラー時は赤背景＋ツールチップで表示する
    const [message, setMessage] = useState<string | undefined>()
    useEffect(() => {
      const err = props.validate?.(value, props)
      setError(data, err, props)
      setMessage(err)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- DefaultCellRenderer と同様に値変更時のみ評価する
    }, [value])
    const isProject = !!data && programItemRowService.isProjectRow(data)
    // プロジェクト行のアイコンはクリックで単票ダイアログを開く（ハンドラがある場合のみ）。
    // 本レンダラは value/iconUrl 変更時のみ再描画するため、クリック時は AgGrid の
    // 最新 node.data を読み、表示名・アイコン以外の編集が反映されないのを防ぐ。
    const handleIconClick =
      isProject && data && onProjectIconClick
        ? () => onProjectIconClick(props.node.data ?? data)
        : undefined
    const iconClickable = !!handleIconClick
    return (
      <Tooltip message={message}>
        <Root>
          {data?.iconUrl ? (
            <Icon
              src={data.iconUrl}
              rounded={isProject}
              clickable={iconClickable}
              onClick={handleIconClick}
            />
          ) : isProject ? (
            // アイコン未設定のプロジェクト行はデフォルトアイコンを表示する
            // （ProgramProjectReport と同じ project_icon_default.svg）
            <Icon
              src={projectDefaultIcon}
              clickable={iconClickable}
              onClick={handleIconClick}
            />
          ) : (
            data && renderTypeIcon(data.type)
          )}
          {/* エラー時はアイコンを除くテキスト部分のみ赤背景にする（プロジェクト計画と同様） */}
          <Label style={{ backgroundColor: message ? '#FADBD8' : undefined }}>
            {value}
          </Label>
        </Root>
      </Tooltip>
    )
  },
  (prevProps, nextProps) => {
    if (prevProps.value !== nextProps.value) return false
    if (prevProps.data?.iconUrl !== nextProps.data?.iconUrl) return false
    return true
  }
)

const Root = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  overflow: 'hidden',
})

// プロジェクトのアイコンは丸く表示する（rounded）。種別アイコン等はそのまま
const Icon = styled('img', {
  shouldForwardProp: prop => prop !== 'rounded' && prop !== 'clickable',
})<{ rounded?: boolean; clickable?: boolean }>(({ rounded, clickable }) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  ...(rounded && { borderRadius: '50%', objectFit: 'cover' }),
  ...(clickable && { cursor: 'pointer' }),
}))

const Label = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // エラー時の赤背景がテキスト領域全体に広がるよう、残り幅いっぱいに伸ばす
  flexGrow: 1,
  minWidth: 0,
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
})
